import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { TableBody, TableHead, TableRow } from '@mui/material';
import { IconTime } from '../../../../../../assets/icons/icon-time';
import { Accordion } from '../../../../../../components';
import { CircularProgress } from '../../../../../../components/circular-progress';
import { useInstallationContext } from '../../../../../../context/installation';
import {
  BucketInfo,
  DynamicMarketPricesWithBucketsQuery,
  DynamicMarketPricesWithBucketsQueryVariables,
} from '../../../../../../data-access/gql-types/graphql';
import { DYNAMIC_MARKET_PRICES_WITH_BUCKETS } from '../../../../../../data-access/queries/meter';
import { HeatmapCell } from '../components/heat-map-cell';
import { HeatmapLegend } from '../components/heatmap-legend';
import { TableCellStyled } from '../components/table-cell';
import { TablePrice } from '../components/table-price';
import { formatHour, getHeatmapColor } from '../utils';

export const ChartDynamic: React.FC = () => {
  const { t } = useTranslation('channel-details');
  const { skipLavvaFetch } = useInstallationContext();
  const { data, loading } = useQuery<DynamicMarketPricesWithBucketsQuery, DynamicMarketPricesWithBucketsQueryVariables>(
    DYNAMIC_MARKET_PRICES_WITH_BUCKETS,
    {
      skip: skipLavvaFetch,
    },
  );

  const dynamicPrices = useMemo(
    () => [...(data?.dynamicMarketPricesWithBuckets.days || [])].reverse(),
    [data?.dynamicMarketPricesWithBuckets.days],
  );

  const renderHeaderCell = (title: string) => (
    <TableCellStyled key={title} align="center" style={{ fontWeight: 600, verticalAlign: 'middle' }}>
      {title === 'hour' ? <IconTime /> : title}
    </TableCellStyled>
  );

  if (loading) return <CircularProgress />;
  if (!dynamicPrices) return null;

  return (
    <>
      <HeatmapLegend />
      {dynamicPrices.map(({ day, records }) => (
        <Accordion key={day} kind="custom" title={day}>
          <TablePrice>
            <TableHead>
              <TableRow>
                {renderHeaderCell('')}
                {records.map((record) => renderHeaderCell(formatHour(record.startingHour)))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                {renderHeaderCell(t('price'))}
                {records.map((record, index) => {
                  const bucketInfos = data?.dynamicMarketPricesWithBuckets.bucketInfos as BucketInfo[] | null;
                  const color = getHeatmapColor(record.priceInkWh, bucketInfos);

                  return (
                    <HeatmapCell
                      key={record.startingHour}
                      value={record.priceInkWh}
                      color={color}
                      orientation="horizontal"
                      roundLeft={index === 0}
                      roundRight={index === records.length - 1}
                    />
                  );
                })}
              </TableRow>
            </TableBody>
          </TablePrice>
        </Accordion>
      ))}
    </>
  );
};
